import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import MainLoader from "../../components/loading/MainLoader";
import CustomBreadcrumb from "../../components/customBreadcrumb/CustomBreadcrumb";
import Title from "../../components/title/Title";
import { styled } from "@mui/system";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Fade,
  Tooltip,
  List,
  Divider,
} from "@mui/material";
import { FaTag } from "react-icons/fa";
import { format } from "date-fns";
import { FaCheck, FaCheckDouble } from "react-icons/fa";
import NoNotifications from "../../assets/images/no_notifications.jpg";
import { BASE_URL } from "../../const/URL";

const Notifications = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<any[]>([]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const response = await axios.get(
        `${BASE_URL}/api/v1/notifications/customer`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.isSuccess) {
        setNotifications(response.data.dataBundle);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <div className="page-container">
      <MainLoader show={loading} />

      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/messages/notifications", title: "Notifications" },
        ]}
      />
      <Title>Notifications</Title>

      {/* Render PromotionalCard for each notification */}
      {notifications.map((notification) => (
        <PromotionalCard
          key={notification.notificationId}
          title={notification.notificationTitle}
          description={notification.notificationContent}
          expiryDate={new Date(notification.expiryDateTime)}
          isRead={notification.loggedUserNotificationStatus}
          notificationId={notification.notificationId}
          notificationUserStatus={notification.notificationUserStatus}
          onButtonClick={() => {
            console.log(
              "Button clicked for notification:",
              notification.notificationId
            );
          }}
        />
      ))}
      {notifications.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <img
            src={NoNotifications}
            alt="No Notifications"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </Box>
      )}
    </div>
  );
};

export default Notifications;

const GradientCard = styled(Card)(({ theme }) => ({
  background:
    "linear-gradient(-21deg,rgb(40, 120, 200) 0%,rgb(72, 184, 106) 100%)",
  marginBottom: theme.spacing(2),
  position: "relative",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
  },
}));

interface PromotionalCardProps {
  title?: string;
  description?: string;
  buttonText?: string;
  isRead?: boolean;
  notificationId?: string;
  expiryDate?: Date;
  notificationUserStatus?: any[];
  onButtonClick?: () => void;
  showTimestamp?: boolean;
}

const PromotionalCard: React.FC<PromotionalCardProps> = ({
  title = "",
  description = "",
  expiryDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
  isRead = false,
  notificationId,
  notificationUserStatus = [],
  onButtonClick,
  showTimestamp = true,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState("");
  const [readStatus, setReadStatus] = useState(isRead);

  //helper function for links
  const renderWithLinks = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#FFD700", textDecoration: "underline" }}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

  // Function to mark the notification as read
  const handleMarkAsRead = async () => {
    if (readStatus || !notificationId) return;

    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      await axios.put(
        `${BASE_URL}/api/v1/notifications/read`,
        { notificationId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Update the local state to reflect the read status
      setReadStatus(true);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  useEffect(() => {
    handleMarkAsRead();
  }, [notificationId]);

  useEffect(() => {
    if (!expiryDate) return;

    const timer = setInterval(() => {
      const now = new Date();
      const difference = expiryDate.getTime() - now.getTime();

      if (difference <= 0) {
        clearInterval(timer);
        setTimeLeft("Notification expired");
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      setTimeLeft(`${days}d ${hours}h remaining`);
    }, 1000);

    return () => clearInterval(timer);
  }, [expiryDate]);

  return (
    <Fade in={isVisible}>
      <GradientCard>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <FaTag style={{ marginRight: "10px", color: "white" }} />
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 700,
                color: "white",
                textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
              }}
            >
              {title}
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: "rgba(255,255,255,0.9)",
              fontWeight: 500,
            }}
          >
            {renderWithLinks(description)}
          </Typography>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {showTimestamp && (
              <Box textAlign="left">
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                  fontWeight={600}
                >
                  {timeLeft}
                </Typography>
              </Box>
            )}

            {showTimestamp && (
              <Box textAlign="right">
                <Typography
                  variant="caption"
                  display="block"
                  fontWeight={600}
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  {`Posted ${format(new Date(), "MMM dd, yyyy")}`}
                </Typography>
              </Box>
            )}

            {/* Tooltip for Read/Unread Status */}
            <Tooltip
              title={
                <Box
                  sx={{
                    maxHeight: "200px",
                    overflow: "auto",
                    padding: "8px",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Read Status
                  </Typography>
                  <List>
                    {notificationUserStatus.map((user) => (
                      <Box key={user.externalUserId}>
                        <Typography variant="body2">
                          {user.externalUserName}:{" "}
                          {user.readStatus ? (
                            <span style={{ color: "#EFB036" }}>Read</span>
                          ) : (
                            <span style={{ color: "#EB5A3C" }}>Unread</span>
                          )}
                        </Typography>
                        {user.readAt && (
                          <Typography variant="caption">
                            Read at:{" "}
                            {format(
                              new Date(user.readAt),
                              "MMM dd, yyyy hh:mm a"
                            )}
                          </Typography>
                        )}
                        <Divider
                          sx={{ backgroundColor: "rgb(255, 255, 255)" }}
                        />
                      </Box>
                    ))}
                  </List>
                </Box>
              }
              arrow
              placement="left"
            >
              <Box>
                {readStatus ? (
                  <FaCheckDouble color="#4FC3F7" />
                ) : (
                  <Typography
                    variant="caption"
                    display="block"
                    fontWeight={600}
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    Unread
                  </Typography>
                )}
              </Box>
            </Tooltip>
          </Box>
        </CardContent>
      </GradientCard>
    </Fade>
  );
};
